import { alpha, svgIconClasses, typographyClasses, buttonBaseClasses, chipClasses } from '@mui/material';
import { gray, brand } from '../ThemePrimitives';
import { mutedSuccessColor, mutedWarningColor, mutedErrorColor } from '../ThemeColors';

export const dataDisplayCustomizations = {
  MuiList: {
    styleOverrides: {
      root: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: '1rem',
          height: '1rem',
          color: theme.palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: 'flex',
          gap: 8,
          padding: '2px 8px',
          borderRadius: theme.shape.borderRadius,
          opacity: 0.7,
          '&.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
            [`& .${svgIconClasses.root}`]: {
              color: theme.palette.text.primary,
            },
            '&:focus-visible': {
              backgroundColor: alpha(theme.palette.action.selected, 0.3),
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.selected, 0.5),
            },
          },
          '&:focus-visible': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        padding: '4px 8px',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: 'none',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5, 1),
        fontWeight: 700,
        transition: 'all 0.2s ease',
        [`& .${chipClasses.label}`]: {
          fontWeight: 700,
          fontSize: '0.6875rem',
          padding: 0,
        },
        variants: [
          {
            props: { color: 'default' },
            style: {
              backgroundColor: alpha(gray[300], 0.4),
              color: gray[700],
              ...theme.applyStyles('dark', {
                backgroundColor: alpha(gray[700], 0.4),
                color: gray[300],
              }),
            },
          },
          {
            props: { color: 'primary' },
            style: {
              backgroundColor: brand[500],
              color: '#FFFFFF',
              ...theme.applyStyles('dark', {
                backgroundColor: brand[400],
                color: '#FFFFFF',
              }),
            },
          },
          {
            props: { color: 'success' },
            style: {
              backgroundColor: mutedSuccessColor[500],
              color: '#FFFFFF',
              ...theme.applyStyles('dark', {
                backgroundColor: mutedSuccessColor[400],
              }),
            },
          },
          {
            props: { color: 'error' },
            style: {
              backgroundColor: mutedErrorColor[500],
              color: '#FFFFFF',
              ...theme.applyStyles('dark', {
                backgroundColor: mutedErrorColor[400],
              }),
            },
          },
          {
            props: { color: 'warning' },
            style: {
              backgroundColor: mutedWarningColor[500],
              color: '#FFFFFF',
              ...theme.applyStyles('dark', {
                backgroundColor: mutedWarningColor[400],
              }),
            },
          },
          {
            props: { size: 'small' },
            style: {
              height: 22,
              [`& .${chipClasses.label}`]: {
                fontSize: '0.6875rem',
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: '0.75rem',
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              height: 28,
              [`& .${chipClasses.label}`]: {
                fontSize: '0.8125rem',
              },
            },
          },
        ],
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: 'flex',
        gap: 4,
        marginRight: 8,
      },
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: 'small',
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: { fontSize: 'small' },
            style: {
              fontSize: '1rem',
            },
          },
          {
            props: { fontSize: 'medium' },
            style: {
              fontSize: '1.25rem',
            },
          },
          {
            props: { fontSize: 'large' },
            style: {
              fontSize: '1.5rem',
            },
          },
        ],
      },
    },
  },
};
